import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ClipboardList, Mail, History, RefreshCw, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORY_LABEL = {
  qc_batch: 'Batch QC', qc_raw: 'RM QC', qc_sample: 'Sample',
  prod_inward: 'Inward/Outward', prod_room: 'Production', prod_weighing: 'Weighing',
  prod_task: 'Task', other: 'Other',
};

const CATEGORY_COLOR = {
  qc_batch: 'bg-sky-900/40 text-sky-300 border-sky-700',
  qc_raw: 'bg-cyan-900/40 text-cyan-300 border-cyan-700',
  qc_sample: 'bg-violet-900/40 text-violet-300 border-violet-700',
  prod_inward: 'bg-amber-900/40 text-amber-300 border-amber-700',
  prod_room: 'bg-orange-900/40 text-orange-300 border-orange-700',
  prod_weighing: 'bg-emerald-900/40 text-emerald-300 border-emerald-700',
  prod_task: 'bg-zinc-800 text-zinc-300 border-zinc-700',
  other: 'bg-zinc-800 text-zinc-300 border-zinc-700',
};

export default function DailySummary() {
  const [tab, setTab] = useState('today');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const loadToday = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/daily-summary`, { params: { date }, withCredentials: true });
      setData(res.data);
    } catch (err) { toast.error('Failed to load summary'); }
    finally { setLoading(false); }
  };

  const loadHistory = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/daily-summary/history`, { params: { days: 30 }, withCredentials: true });
      setHistory(res.data?.items || []);
    } catch (err) { toast.error('Failed to load history'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (tab === 'today') loadToday();
    else loadHistory();
    // eslint-disable-next-line
  }, [tab, date]);

  const sendNow = async () => {
    if (!window.confirm(`Send summary for ${date} to all users + their heads + sales@tirthindustries.co?`)) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/daily-summary/send-now`, { date }, { withCredentials: true });
      const n = res.data?.users_processed || 0;
      if (n === 0) toast.info('No activity to email for this date');
      else toast.success(`Emailed ${n} user summary${n > 1 ? 'ies' : 'y'}`);
      loadHistory();
    } catch (err) { toast.error(err.response?.data?.detail || 'Send failed'); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="daily-summary-page" className="p-6 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-100 flex items-center gap-2">
            <ClipboardList className="text-emerald-400" size={24} /> Daily Summary
          </h1>
          <p className="text-sm text-zinc-400 mt-1">Per-user activity rollup · auto-emailed 20:00 IST daily · history kept 30 days.</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            data-testid="ds-date"
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 text-zinc-100 text-xs rounded-lg px-3 py-1.5"
          />
          <button
            data-testid="ds-send-now"
            onClick={sendNow}
            disabled={sending}
            className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-700 text-white text-xs font-semibold flex items-center gap-1.5"
          ><Mail size={13} /> {sending ? 'Sending…' : 'Send Now'}</button>
          <button
            onClick={() => (tab === 'today' ? loadToday() : loadHistory())}
            data-testid="ds-refresh"
            className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700"
          ><RefreshCw size={14} /></button>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          data-testid="ds-tab-today"
          onClick={() => setTab('today')}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium border flex items-center gap-1.5 ${tab === 'today' ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-zinc-800 border-zinc-700 text-zinc-300'}`}
        ><Clock size={13} /> Live ({date === new Date().toISOString().slice(0,10) ? 'Today' : date})</button>
        <button
          data-testid="ds-tab-history"
          onClick={() => setTab('history')}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium border flex items-center gap-1.5 ${tab === 'history' ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-zinc-800 border-zinc-700 text-zinc-300'}`}
        ><History size={13} /> History (30 days)</button>
      </div>

      {tab === 'today' && (
        loading ? <div className="text-sm text-zinc-500 animate-pulse">Loading…</div>
        : !data?.users?.length ? (
          <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-8 text-center text-zinc-500 text-sm">
            No activity logged for {date} yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.users.map(u => <UserSummaryCard key={u.user_id} u={u} />)}
          </div>
        )
      )}

      {tab === 'history' && (
        loading ? <div className="text-sm text-zinc-500 animate-pulse">Loading…</div>
        : history.length === 0 ? (
          <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-8 text-center text-zinc-500 text-sm">No summaries emailed yet.</div>
        ) : (
          <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-zinc-800/60 text-zinc-400 uppercase text-[10px]">
                <tr>
                  <th className="px-3 py-2 text-left">Sent At</th>
                  <th className="px-3 py-2 text-left">User</th>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Activity</th>
                  <th className="px-3 py-2 text-left">Recipients</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.id} data-testid={`ds-hist-${h.id}`} className="border-t border-zinc-800 hover:bg-zinc-800/40">
                    <td className="px-3 py-2 text-zinc-400 whitespace-nowrap">{h.created_at?.slice(0, 16).replace('T', ' ')}</td>
                    <td className="px-3 py-2 text-zinc-100 font-medium">{h.user_name}</td>
                    <td className="px-3 py-2 text-zinc-400">{h.date}</td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1 flex-wrap">
                        {Object.entries(h.counts || {}).map(([k, v]) => (
                          <span key={k} className={`text-[10px] px-1.5 py-0.5 rounded border ${CATEGORY_COLOR[k] || CATEGORY_COLOR.other}`}>
                            {CATEGORY_LABEL[k] || k}: {v}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-zinc-400 text-[11px]">{(h.recipients || []).join(', ')}</td>
                    <td className="px-3 py-2">
                      {h.email_status?.sent ? (
                        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-md border bg-green-900/40 text-green-300 border-green-700">
                          <CheckCircle2 size={10} /> Sent
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-md border bg-amber-900/40 text-amber-300 border-amber-700" title={h.email_status?.reason}>
                          Failed
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

function UserSummaryCard({ u }) {
  return (
    <div data-testid={`ds-user-${u.user_id}`} className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="font-bold text-zinc-100">{u.user_name}</div>
          <div className="text-[11px] text-zinc-500">{u.user_role} · {u.user_email}</div>
        </div>
        <div className="text-[10px] text-zinc-500">{u.activities.length} entries</div>
      </div>
      <div className="flex flex-wrap gap-1 mb-3">
        {Object.entries(u.counts || {}).map(([k, v]) => (
          <span key={k} className={`text-[10px] px-1.5 py-0.5 rounded border ${CATEGORY_COLOR[k] || CATEGORY_COLOR.other}`}>
            {CATEGORY_LABEL[k] || k}: {v}
          </span>
        ))}
      </div>
      <div className="space-y-1 max-h-60 overflow-y-auto">
        {u.activities.slice(0, 20).map((a, i) => (
          <div key={i} className="flex gap-2 text-[11px] text-zinc-300 border-b border-zinc-800 pb-1">
            <span className="text-zinc-500 font-mono whitespace-nowrap">{a.time}</span>
            <span className={`text-[10px] px-1 py-0 rounded border shrink-0 ${CATEGORY_COLOR[a.category] || CATEGORY_COLOR.other}`}>{CATEGORY_LABEL[a.category] || a.category}</span>
            <span className="text-zinc-300">{a.summary}</span>
          </div>
        ))}
        {u.activities.length > 20 && <div className="text-[11px] text-zinc-500 text-center mt-2">+ {u.activities.length - 20} more</div>}
      </div>
    </div>
  );
}
